import React, { useState, useEffect } from 'react';

import { observer } from 'mobx-react-lite';

import subHours from 'date-fns/sub_hours';

import PageLoader from '@vromo/ui/components/PageLoader';
import LiveStats from './LiveStats';
import GeneralStats from './GeneralStats';

import last from 'lodash/last';

import SignupSurvey from '../SignupSurvey';
import { useToasts } from '@vromo/ui/components/Toasts';
import { Job } from '@vromo/models';
import useBatchFetchJobs from './batchFetchJobs';
import { groupBy, range } from 'lodash';
import { startOfHour } from 'date-fns';

export type JobSummaryChartData = {
    name: number;
    completed: number;
    cancelled: number;
};

export type GroupJob = {
    group: number;
    completeJobs: number;
};

export type JobStats = {
    jobCount: number;
    jobsCompleted: number;
    jobsCancelled: number;
    linksOpened: number;
    completedOnTime: string;
    jobsPerGroup: GroupJob[];
    jobSummaryChartData: JobSummaryChartData[];
};

export default observer(function Dashboard() {
    const showToast = useToasts();

    const [jobStats, setJobStats] = useState<JobStats | Record<string, never>>({});

    const [startDate, endDate] = [subHours(new Date(), 24), new Date()];

    const { batchFetchJobs, loadingJobs } = useBatchFetchJobs();

    const onTimePercent = (completedJobs: Job[], jobCount: number) => {
        const lateJobs = completedJobs.filter((j) => {
            const deadlineTime = last(j.tasks?.items)?.timeframe?.by;
            const completionTime = last(j.tasks?.items)?.completed?.time;

            return deadlineTime && completionTime && deadlineTime < completionTime;
        });

        return lateJobs.length ? ((jobCount - lateJobs.length) / jobCount) * 100 : 100;
    };

    const calculateTrackingLinksOpened = (jobs: Job[]) => {
        return jobs.filter(({ stages }) => {
            if (!stages) {
                return false;
            }
            return Object.keys(stages).some((stage) => stages[stage]?.analytics?.clicksTotal);
        }).length;
    };

    const jobSummaryChartData = (completedJobs: Job[], cancelledJobs: Job[]) => {
        const hours = range(24, -1).map((it) => startOfHour(subHours(endDate, it)).getTime());

        return hours.map((h) => {
            return {
                name: h,
                completed: completedJobs.filter((j) => {
                    const completedTime = last(j.tasks?.items)?.completed?.time;

                    return completedTime && h === startOfHour(completedTime).getTime();
                }).length,
                cancelled: cancelledJobs.filter(
                    (j) => j.finished && h === startOfHour(j.finished).getTime()
                ).length
            };
        });
    };

    useEffect(() => {
        const getAllJobs = async () => {
            try {
                const jobs = await batchFetchJobs({
                    params: {
                        _all_attr__include: false,
                        _all_dispatches__include: false,
                        _all_route__include: false,
                        _all_stages__all_analytics_clicksTotal__include: true
                    },
                    startDate,
                    endDate
                });

                const completedJobs = jobs.filter((j) => j.status === 'completed');
                const cancelledJobs = jobs.filter((j) => j.status === 'cancelled');

                const jobsPerGroup = Object.entries(groupBy(jobs, (j) => j.group)).map(
                    ([groupId, groupJobs]) => ({
                        group: parseInt(groupId),
                        completeJobs: groupJobs.filter((j) => j.status === 'completed').length
                    })
                );

                const stats = {
                    jobCount: jobs.length,
                    jobsCompleted: completedJobs.length,
                    jobsCancelled: cancelledJobs.length,
                    linksOpened: calculateTrackingLinksOpened(jobs),
                    completedOnTime: completedJobs.length
                        ? `${onTimePercent(completedJobs, jobs.length).toFixed(0)}%`
                        : '-',
                    jobsPerGroup,
                    jobSummaryChartData: jobSummaryChartData(completedJobs, cancelledJobs)
                };

                setJobStats(stats);
            } catch (e) {
                console.error(e);
                showToast('Something went wrong with fetching your jobs.', 'error');
            }
        };
        getAllJobs();
    }, []);

    return (
        <div
            style={{
                height: '100%',
                flex: 1,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
            }}>
            <PageLoader loading={loadingJobs} />
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    maxWidth: '1500px',
                    padding: '1rem 0rem',
                    margin: 'auto',
                    flexDirection: 'column'
                }}>
                <LiveStats loading={loadingJobs} />
                <GeneralStats jobStats={jobStats} loading={loadingJobs} />
            </div>
            <SignupSurvey />
        </div>
    );
});
